// not using { cn } from '@/lib/utils' because of aggressive merging and getting conflict with color and fontsize
import { clsx } from 'clsx';
import React from 'react';

import { Size } from '@/hooks/useScreenSize';

export type ButtonSize = Size | 'icon';
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'exceptional' | 'default';

export type ColorMode = 'dark' | 'light';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  iconLeft?: React.ReactNode | string;
  iconRight?: React.ReactNode | string;
  iconLeftWrapperClassName?: string;
  iconRightWrapperClassName?: string;
  loading?: boolean;
  colorMode?: ColorMode;
}

// we don't have padding-x as per figma button component but we are overriding it in every place.
// so keeping a minimal padding-x for good looking if not overriding.
const sizeClasses: Record<ButtonSize, string> = {
  sm: 'text-fontSize-cta-button-sm h-[32px] py-1 px-3 border-[1px]',
  md: 'text-fontSize-cta-button-md h-[42px] py-1.5 px-4 border-[1.3px]',
  lg: 'text-fontSize-cta-button-md h-[54px] py-2 px-5 border-[1.69px]',
  xl: 'text-fontSize-cta-button-md h-[54px] py-2 px-5 border-[1.69px]',
  xxl: 'text-fontSize-cta-button-xl h-[70px] py-2.5 px-6 border-[2.197px]',
  icon: 'size-10 p-0',
};

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'w-6 h-6', // ~24px
  md: 'w-9 h-9', // ~36px
  lg: 'w-11 h-11', // ~44px
  xl: 'w-11 h-11', // ~44px
  xxl: 'w-14 h-14', // ~56px
  icon: '',
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-color-background-button-primary text-color-content-text-primary shadow-[0px_3px_7px_rgba(0,0,0,0.25)] border-none',
  secondary:
    'bg-color-background-button-secondary text-color-content-text-primary border border-color-border-button-secondary shadow-[0px_2px_4px_rgba(0,0,0,0.25)]',
  tertiary:
    'bg-color-background-button-tertiary-100 border border-color-border-button-tertiary text-color-blue-brightBlue100 shadow-[0px_2px_4px_rgba(0,0,0,0.25)]',
  exceptional: 'bg-color-background-button-tertiary-8 text-color-content-text-primary border-none',
  default:
    'bg-color-background-button-tertiary-100 text-color-blue-brightBlue100 shadow-[0px_2px_4px_rgba(0,0,0,0.25)]',
};

function renderIcon(
  icon: React.ReactNode | string | undefined,
  size: ButtonSize,
  wrapperClassName?: string
) {
  if (!icon) return null;
  if (typeof icon === 'string') {
    return (
      <img
        src={icon}
        alt=""
        aria-hidden
        className={clsx('mr-[8px] inline-block h-[18px] w-[18px] object-contain')}
      />
    );
  }
  return (
    <span
      className={clsx(
        iconSizeClasses[size],
        wrapperClassName,
        'inline-flex items-center justify-center'
      )}
    >
      {icon}
    </span>
  );
}

/**
 * Reusable Button component based on the Figma design.
 * - Supports sizes: sm, md, lg, xl, icon
 * - Supports variants: primary, secondary, tertiary, exceptional
 * - Defaults to size 'sm' and variant 'primary'
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = 'sm',
      variant = 'default',
      iconLeft,
      iconRight,
      iconLeftWrapperClassName,
      iconRightWrapperClassName,
      children,
      className = '',
      disabled,
      loading,
      colorMode,
      ...props
    },
    ref
  ) => {
    const sizeClass = sizeClasses[size];
    const variantClass = variantClasses[variant];

    const base = `inline-flex items-center justify-center rounded-radius-7xl leading-[125%] whitespace-nowrap font-[700] hover:opacity-90 ${sizeClass} ${variantClass}`;
    const stateClass =
      disabled || loading ? '!opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer';

    return (
      <button
        ref={ref}
        type={props.type === 'submit' ? 'submit' : props.type === 'reset' ? 'reset' : 'button'}
        className={clsx(base, stateClass, className)}
        disabled={disabled || loading}
        {...props}
      >
        {iconLeft ? (
          <span className="flex items-center">
            {renderIcon(iconLeft, size, iconLeftWrapperClassName)}
          </span>
        ) : null}
        {children}
        {iconRight ? (
          <span className="flex items-center">
            {renderIcon(iconRight, size, iconRightWrapperClassName)}
          </span>
        ) : null}
      </button>
    );
  }
);

export default Button;
